import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faCircleInfo, faSave } from '@fortawesome/free-solid-svg-icons';
import { BehaviorSubject } from 'rxjs';
import { IBoxGroup } from 'src/app/models/interfaces/box-group.interface';
import { MemoryService } from 'src/app/services/memory.service';
import { ViewsService } from 'src/app/services/views.service';
import { FormMode } from '../../../controls/common/form-mode';
import { FormFocusDirective } from '../../../controls/directives/form-focus.directive';
import { Controls } from '../../../controls/forms/managed-form/ngx-sub-form-utils';
import { DataInput } from '../../../controls/forms/management-form/form.decorators';
import { ManagementFormComponent } from '../../../controls/forms/management-form/management-form';
import { BoxGroupFormContext } from '../box-group-form-context';

@Component({
    selector: 'app-box-group-form',
    templateUrl: './box-group-form.component.html',
    styleUrls: ['./box-group-form.component.scss'],
    imports: [FormsModule, FormFocusDirective, ReactiveFormsModule, MatFormFieldModule, MatInputModule, FaIconComponent, MatTooltipModule, MatButtonModule, NgClass]
})
export class BoxGroupFormComponent extends ManagementFormComponent<IBoxGroup, IBoxGroup> implements OnInit, OnChanges {
    public faSave = faSave;
    public faCircleInfo = faCircleInfo;

    /**
     * Mode d'affichage du formulaire (consultation / edition / création)
     */
    public FormMode = FormMode;

    /**
     * Infos de l'utilisateur à afficher dans le formulaire.
     */
    @DataInput()
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('box')
    public dataInput: Required<IBoxGroup> | null | undefined;

    /**
     * Contexte du formulaire (mode d'affichage ...).
     */
    public _context: BoxGroupFormContext;

    /**
     * Utilisé pour propager des changements de contexte.
     */
    public _context$: BehaviorSubject<BoxGroupFormContext> = new BehaviorSubject(null);

    /**
     * Appellé par le composant parent pour specifier un nouveau contexte.
     */
    @Input()
    set context(value: BoxGroupFormContext) {
        this._context$.next(value);
    }

    /*
     * Indique si le composant est en mode debug (affiche des informations pour le developpeur)
     */
    public debugMode = false;

    /**
     * Emis lorsque le formulaire est mis à jour (clic sur le bouton sauver)
     */
    // eslint-disable-next-line @angular-eslint/no-output-rename
    @Output('boxUpdated')
    public dataOutput: EventEmitter<IBoxGroup> = new EventEmitter();

    /**
     * Emis lorsque le formulaire est annuler(clic sur le bouton annuler)
     */
    @Output()
    public canceled: EventEmitter<boolean> = new EventEmitter();

    constructor(
        private viewsService: ViewsService,
        private memoryService: MemoryService
    ) {
        super();
    }

    /**
     * Initialise le composant.
     */
    override ngOnInit() {
        super.ngOnInit();

        this.formGroup.get('viewId').setValue(this.memoryService.getSelectedView()?.id);

        // S'abonne aux changements de contexte
        this._context$.subscribe((x) => {
            this._context = x;
            this.dataInput$.next(x.item);
        });
    }

    /**
     * Utilisé pour rendre le champ id read-only
     */
    ngOnChanges() {
        this.formGroup.get('id').disable();

        // L'ordre est non modifiable via le formulaire, la mise à jour se fait uniquement via la liste
        this.formGroup.get('order').disable();
    }

    /**
     * Retourne la liste des contrôles du formulaire
     */
    protected getFormControls(): Controls<IBoxGroup> {
        return {
            id: new UntypedFormControl(null, Validators.required),
            name: new UntypedFormControl(null, Validators.required),
            viewId: new UntypedFormControl(null),
            order: new UntypedFormControl(null),
        };
    }

    /**
     *  Appellé lorsque l'utilisateur clique sur le bouton 'sauver'
     */
    public override manualSave(): void {
        // Force l'id de la vue car il n'est pas fourni à l'API car le champ n'existe pas (défini via la liste déroulante de choix de la vue)
        this.dataValue.viewId = this.memoryService.getSelectedView()?.id;

        // Force l'ordre car il n'est pas fourni à l'API car le champ est inactif
        this.dataValue.order = this.formGroup.get('order') ? this.formGroup.get('order').value : 0;

        super.manualSave();
    }

    public cancel(): void {
        this.canceled.emit(true);
    }
}
